/* eslint-disable max-lines */
import { AudioWaveform } from '@components/editors/audio-waveform.tsx';
import { usePrompt } from '@components/editors/confirm/prompt.tsx';
import { TranscriptBody } from '@components/editors/transcript-editor/body/transcript-body.tsx';
import TranscriptAbout from '@components/editors/transcript-editor/transcript-about.tsx';
import { SpeakersVoicePanel } from '@components/editors/shared/voice-profiles/speakers-voice-panel.tsx';
import { TranscriptPlayer } from '@components/editors/transcript-editor/transcript-player.tsx';
import { TranscriptFindReplaceHost } from '@components/editors/transcript-editor/transcript-find-replace-host.tsx';
import { AnchoredOverlay } from '@components/editors/shared/find-replace/anchored-overlay.tsx';
import { TranscriptMediaPlayer } from '@components/editors/transcript-editor/transcript-media-player.tsx';
import { FloatingVideoOverlay } from '@components/editors/shared/media/floating-video-overlay.tsx';
import { SplitTranscriptSurface } from '@components/editors/transcript-editor/split-view/split-transcript-surface.tsx';
import { useThemeContext } from '@utils/hooks/use-theme-context.ts';
import { useThemeVars } from '@utils/hooks/use-theme-vars.ts';
import { useTranscript } from '@utils/providers/transcript-context.tsx';
import { EditorProvider } from '@utils/providers/editor-context.tsx';
import { useTranscriptHotkeys } from '@utils/providers/transcript-hotkey-context.tsx';
import { useConcurrentEditPolling } from '@utils/hooks/use-concurrent-edit-polling.ts';
import ConcurrentEditBanner from '@components/editors/concurrent-edit-banner.tsx';
import cn from 'classnames';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useQueryClient } from 'react-query';
import toast from 'react-hot-toast';
import { useSearchParams } from 'react-router-dom';
import { QueryKeys } from '@utils/api/query-keys.ts';
import { useGetSessionDocuments } from '@utils/api/documents/use-get-session-documents.ts';
import {
  SplitDirtyProvider,
  useSplitDirty
} from '@utils/providers/split-dirty-tracker.tsx';
import { useConfirm } from '@utils/providers/confirm-context.tsx';
import { useTranslation } from 'react-i18next';
import { useSidebarCollapsed } from '@utils/hooks/use-sidebar-collapsed.ts';
import { MEDIA_QUERIES, useMediaQuery } from '@utils/hooks/use-media-query.tsx';
import { CommentFilterProvider } from '@components/editors/shared/comments/comments-filter-context.tsx';
import { resolveTranslationFromLang } from '@utils/editors/selected-translation.ts';

type TranscriptProps = {
  sessionId: string;
  documentId: string;
};

export default function TranscriptMain(props: TranscriptProps) {
  // #2237: `SplitDirtyProvider` wraps the whole shell so the inner
  // body (which also hosts the TranslationTranscriptProvider) can
  // report its dirty flag up to the shell's usePrompt / tab-close
  // warning without an imperative handle.
  return (
    <SplitDirtyProvider>
      <TranscriptMainBody {...props} />
    </SplitDirtyProvider>
  );
}

function TranscriptMainBody({ sessionId, documentId }: TranscriptProps) {
  const {
    session,
    document: transcriptDocument,
    documentResult,
    autoSave,
    isDirty,
    saveStatus,
    saveDocument,
    reloadFromServer,
    search,
    setEditor: sourceSetEditor,
    setStartStopWord: sourceSetStartStopWord
  } = useTranscript();

  const queryClient = useQueryClient();
  const { anyDirty: splitAnyDirty } = useSplitDirty();
  // #2235: split layout is derived from `?lang=XX` in the URL, not
  // from a persisted boolean toggle. `selectedTranslationId` (below)
  // picks which translation lives on the right pane; when it's
  // `null` the editor renders as a single column. The URL carries
  // the language code so links survive reloads and share correctly.
  const [searchParams, setSearchParams] = useSearchParams();
  const { show: showConfirm } = useConfirm();
  const { t } = useTranslation();

  // #2237: pixel-level scroll-sync is gone. The split surface now
  // renders a single Virtuoso with paired source|translation rows,
  // so both sides share one scroll container by construction.

  // Fetch session documents for split view
  const { data: sessionDocs } = useGetSessionDocuments(sessionId || undefined);
  const translatedDocuments = useMemo(
    () =>
      (sessionDocs?.result ?? []).filter(
        doc => doc.id !== documentId && doc.type === 'document'
      ),
    [sessionDocs, documentId]
  );
  const hasTranslations = translatedDocuments.length > 0;

  // #2235: resolve `?lang=XX` → translation id. The URL carries a
  // language code (`EN`, `ES`, …) rather than the opaque translation
  // id so links are readable and don't break if the translation id
  // changes server-side. See the unit tests next to the resolver.
  const urlLang = searchParams.get('lang');
  const { translationId: selectedTranslationId, stale } = useMemo(
    () => resolveTranslationFromLang(urlLang, translatedDocuments),
    [urlLang, translatedDocuments]
  );

  // Stale-link fallback: the URL asked for a language we no longer
  // have a translation for (deleted / renamed). Flash a toast and
  // strip the param so the user lands on the original cleanly.
  const staleWarnedRef = useRef(false);
  useEffect(() => {
    if (!urlLang) {
      staleWarnedRef.current = false;
      return;
    }
    // Wait for translations to load before deciding the lang is stale.
    if (!sessionDocs) return;
    if (!stale || staleWarnedRef.current) return;
    staleWarnedRef.current = true;
    toast(t('translation-not-available'));
    const next = new URLSearchParams(searchParams);
    next.delete('lang');
    setSearchParams(next, { replace: true });
  }, [urlLang, stale, sessionDocs, searchParams, setSearchParams, t]);

  // Setter used by the Change-Language modal. Picking the source doc
  // (same id as the primary `documentId`) clears the URL param;
  // picking a translation writes its language code.
  const applySelectedTranslation = useCallback(
    (docId: string | null) => {
      const next = new URLSearchParams(searchParams);
      if (!docId || docId === documentId) {
        next.delete('lang');
      } else {
        const translation = translatedDocuments.find(d => d.id === docId);
        if (translation?.language) {
          next.set('lang', translation.language.toUpperCase());
        } else {
          next.delete('lang');
        }
      }
      setSearchParams(next);
    },
    [searchParams, setSearchParams, translatedDocuments, documentId]
  );

  // #2237 decision: on a language switch with unsaved edits, SAVE
  // FIRST (quiet, matches autosave schedule) rather than asking the
  // user. The autosave timer in the translation provider will also
  // flush, but kicking the source's save synchronously keeps the
  // outgoing document safe before the lang param changes.
  const handleTranslationChange = useCallback(
    (docId: string) => {
      if (isDirty) {
        saveDocument(false);
      }
      applySelectedTranslation(docId);
    },
    [isDirty, saveDocument, applySelectedTranslation]
  );
  // Reference these to keep the imports valid; the confirm UX is
  // replaced by save-first per the ticket decision.
  void showConfirm;
  void t;

  // Final layout gate: split only when the URL language actually
  // resolved to one of this session's translations.
  const isSplitView = !!selectedTranslationId && hasTranslations;

  // #2237: dual save + the separate translation Cmd/Ctrl+S hook are
  // gone. The translation provider now listens to its own Cmd+S and
  // runs its own autosave timer. The source still saves itself here.

  const { hasRemoteEdit, dismiss, reload } = useConcurrentEditPolling({
    sessionId,
    documentId,
    initialLastModified: documentResult?.result?.last_modified ?? new Date(),
    isSaving: saveStatus === 'saving',
    enabled: !!documentResult && !documentResult.result.finished
  });

  const hasRemoteEditRef = useRef(false);
  hasRemoteEditRef.current = hasRemoteEdit;

  const handleReload = useCallback(async () => {
    const freshDoc = await reload();
    if (freshDoc) {
      queryClient.setQueryData(
        QueryKeys.document(sessionId, documentId),
        freshDoc
      );
      reloadFromServer(freshDoc);
    }
  }, [reload, queryClient, sessionId, documentId, reloadFromServer]);

  const handleSaveMyChanges = useCallback(() => {
    // Source saves here; the translation provider handles its own
    // Cmd+S / autosave inside TranslationTranscriptProvider (#2237).
    saveDocument(true);
    dismiss();
  }, [saveDocument, dismiss]);

  const [enabled, setEnabled] = useState<boolean>(
    documentResult?.result?.finished ?? false
  );

  const { vars, cls } = useThemeVars();
  const { getColour } = useThemeContext();

  // Fixed-position transport + waveform used to span the full viewport
  // (`left-0 w-full`). Since #2230 the app sidebar rail sits over that
  // left edge, so shift them by the current rail width. Below the
  // large-tablet breakpoint the rail is hidden (drawer) → offset is 0.
  // Values come from sidebar-shell.tsx (w-14 collapsed / w-[16.5rem]
  // expanded).
  const [sidebarCollapsed] = useSidebarCollapsed();
  const [largeTabletAndAbove] = useMediaQuery(
    `(min-width: ${MEDIA_QUERIES.LARGE_TABLET})`
  );
  const sidebarOffset = largeTabletAndAbove ? (sidebarCollapsed ? 56 : 264) : 0;

  // Auto-save: when isDirty becomes true, wait 30s of idle then save.
  // If the user keeps editing, the timer resets. A hard cap of 2 minutes
  // ensures we save even during continuous editing.
  const autoSaveTimerRef = useRef<NodeJS.Timeout | null>(null);
  const firstDirtyRef = useRef<number>(0);
  const autoSaveRef = useRef(autoSave);
  autoSaveRef.current = autoSave;

  useEffect(() => {
    if (!isDirty) {
      firstDirtyRef.current = 0;
      if (autoSaveTimerRef.current) {
        clearTimeout(autoSaveTimerRef.current);
        autoSaveTimerRef.current = null;
      }
      return;
    }

    const now = Date.now();
    if (firstDirtyRef.current === 0) {
      firstDirtyRef.current = now;
    }

    // Clear previous timer — user is still editing
    if (autoSaveTimerRef.current) {
      clearTimeout(autoSaveTimerRef.current);
    }

    // Save after 30s of idle, but never wait longer than 2 minutes total
    const elapsed = now - firstDirtyRef.current;
    const delay = Math.min(30_000, Math.max(0, 120_000 - elapsed));

    autoSaveTimerRef.current = setTimeout(() => {
      // Don't autosave to server if another user saved — avoid
      // overwriting their changes. The translation pane runs its own
      // autosave timer inside its provider (#2237).
      if (!hasRemoteEditRef.current) {
        autoSaveRef.current();
      }
      firstDirtyRef.current = 0;
    }, delay);

    return () => {
      if (autoSaveTimerRef.current) {
        clearTimeout(autoSaveTimerRef.current);
      }
    };
  }, [isDirty, transcriptDocument]);

  // #2237: dirty state comes from either the source transcript or
  // the translation pane via the SplitDirtyProvider broadcast.
  usePrompt({ isDirty: isDirty || splitAnyDirty });

  const { searchOpen, closeSearch } = useTranscriptHotkeys();

  return (
    // #2290 fix — EditorProvider used to live inside TranscriptBody,
    // which meant the Find & Replace popover (rendered as a sibling)
    // was outside its scope. `useEditorContext()` returned the no-op
    // fallback, so Replace All silently did nothing. Hoisting the
    // provider here puts every mounted paragraph editor into one
    // shared `state.editors` map that both the body AND the popover
    // can read.
    <EditorProvider>
      <CommentFilterProvider>
        <TranscriptAbout
          enabled={enabled}
          setEnabled={setEnabled}
          hasSplitTranslations={hasTranslations}
          splitTranslatedDocuments={translatedDocuments}
          selectedTranslationId={selectedTranslationId}
          onTranslationChange={handleTranslationChange}
        />

        {hasRemoteEdit && (
          <ConcurrentEditBanner
            onReload={handleReload}
            onDismiss={dismiss}
            onSaveMyChanges={handleSaveMyChanges}
            hasUnsavedChanges={isDirty}
          />
        )}

        <div
          data-source-dirty={isDirty ?? false}
          data-translation-dirty={splitAnyDirty}
          className={cn('shadow h-[calc(100vh-36px)] overflow-y-auto pb-16')}
          style={{
            paddingBottom: session?.wave_info_url ? '125px' : '55px',
            backgroundColor: getColour('baseLayer')
          }}
        >
          {/* #2355 — recognition status and "Run again" for the transcript
              editor. Renders nothing unless voice profiles are available to
              this user, so editors that never use the feature see no change. */}
          {session && (
            <div className="px-6 pt-2">
              <SpeakersVoicePanel session={session} documentId={documentId} />
            </div>
          )}
          {transcriptDocument &&
            (isSplitView && session && selectedTranslationId ? (
              <SplitTranscriptSurface
                session={session}
                sessionId={sessionId}
                translationDocumentId={selectedTranslationId}
                sourceSearch={search}
                onEditorFocus={sourceSetEditor}
                onStartStopWordChange={sourceSetStartStopWord}
              />
            ) : (
              <TranscriptBody
                sessionId={sessionId}
                documentId={documentId}
                enabled={enabled}
              />
            ))}
        </div>

        {session && session.wave_info_url && (
          <div
            className="fixed border transition-[left,width] duration-150"
            style={{
              left: sidebarOffset,
              width: `calc(100% - ${sidebarOffset}px)`,
              bottom: '49px',
              backgroundColor: getColour('lightestLayer'),
              border: getColour('borderPrimary')
            }}
          >
            <AudioWaveform session={session} />
          </div>
        )}

        <div
          className={cn(
            'fixed bottom-0 p-1 transition-[left,width] duration-150',
            cls.cardBg
          )}
          style={{
            ...vars,
            left: sidebarOffset,
            width: `calc(100% - ${sidebarOffset}px)`
          }}
        >
          <TranscriptPlayer session={session} />
        </div>

        {/* Floating video overlay — draggable, bottom-right by default,
          per-session position. In split view the default flips to
          bottom-left so the player doesn't start on top of the
          translation pane (the one being edited) — #2233 reopen
          comment. Audio-only sessions render nothing (no empty video
          box). */}
        <FloatingVideoOverlay
          hasVideo={!!session?.media_url && session?.media_type !== 'audio'}
          sessionId={session?.session_id}
          defaultCorner={isSplitView ? 'bottom-left' : 'bottom-right'}
        >
          <TranscriptMediaPlayer />
        </FloatingVideoOverlay>

        {/* Audio-only sessions still need the media element mounted so the
          transport bar can drive it; render it off-screen. */}
        {(!session?.media_url || session?.media_type === 'audio') && (
          <div className="hidden">
            <TranscriptMediaPlayer />
          </div>
        )}

        {/* Find & Replace popover — anchored directly under the pinned
          search trigger (image 24). AnchoredOverlay reads the trigger's
          rect via `[data-action-key="search"]` and follows on reflow. */}
        {/* Split view mounts its own popover inside
            `SplitTranscriptSurface` so the host runs with the
            TRANSLATION `useTranscript()` scope — that's the editable
            pane, so matches + Replace All target the right document.
            Mounting both would double-render the popover. */}
        {searchOpen && !isSplitView && (
          <AnchoredOverlay anchorKey="search">
            <TranscriptFindReplaceHost onClose={closeSearch} />
          </AnchoredOverlay>
        )}
      </CommentFilterProvider>
    </EditorProvider>
  );
}
